import { Ref, useEffect, useEffectEvent, useState } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { ItemNoAndTypeWithOrderedDetails } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  isLoading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import { useAllAttachedItems } from "@coworker/apprestructured/src/task-management/services/useAllAttachedItems";
import LoadingCreatorFragment from "../InvalidCreatorFragment/InvalidCreatorFragment";
import { ListItem } from "../../../SelectListItem/SelectListItem";
import { ArticlesQuantity } from "../../../ArticlesQuantity/ArticlesQuantity";
import { initializeOrderedDetails } from "@coworker/apprestructured/src/task-management/util";

interface VisibilityCondition {
  field: string;
  value: any;
  operator: "in";
}

export interface ArticlesQuantityCreatorFragmentConfigProps {
  title: string;
  field: string;
  visibility: VisibilityCondition;
}

interface ArticlesQuantityCreatorFragmentProps {
  configProps: ArticlesQuantityCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
}

function shouldShowField(
  task: CreatorFragmentCommonProps["task"],
  visibility: VisibilityCondition
): boolean {
  const currentValue = getByPath(task, visibility.field) as ListItem | null;

  if (visibility.operator === "in") {
    return Array.isArray(visibility.value)
      ? visibility.value.includes(currentValue?.value)
      : false;
  }

  return true;
}

const ArticlesQuantityCreatorFragment = ({
  configProps,
  commonProps,
}: ArticlesQuantityCreatorFragmentProps) => {
  const { field, visibility } = configProps;
  const { task, taskType, onTaskChange } = commonProps;

  const itemNosAndTypeWithOrderedDetailsFromTask =
    validateInitialDetailsFieldValue<ItemNoAndTypeWithOrderedDetails[]>(
      getByPath(task, field),
      field,
      taskType
    );

  const normalizedItemsOrderedDetails = initializeOrderedDetails(
    itemNosAndTypeWithOrderedDetailsFromTask
  );

  const initializeItemsOrderedDetails = useEffectEvent(() => {
    if (
      !Array.isArray(itemNosAndTypeWithOrderedDetailsFromTask) ||
      isLoading(itemNosAndTypeWithOrderedDetailsFromTask)
    ) {
      return;
    }

    const hasOrderedFields = itemNosAndTypeWithOrderedDetailsFromTask.some(
      (item) => "pieces_ordered" in item || "ptags_ordered" in item
    );

    if (!hasOrderedFields) {
      const itemsWithOrderedDetails =
        itemNosAndTypeWithOrderedDetailsFromTask.map((item) => ({
          ...item,
          pieces_ordered: { quantity: item.pieces, completed: false },
          ptags_ordered: { quantity: item.pieces, completed: false },
        }));

      onTaskChange({ details: { items: itemsWithOrderedDetails } });
    }
  });

  useEffect(() => {
    initializeItemsOrderedDetails();
  }, []);

  const [piecesQuantityOverrides, setPiecesQuantityOverrides] = useState<
    Record<string, number>
  >({});

  const itemsOrderedDetails = Array.isArray(normalizedItemsOrderedDetails)
    ? normalizedItemsOrderedDetails.map((item) => ({
        ...item,
        pieces_ordered: {
          quantity:
            piecesQuantityOverrides[item.no] ?? item.pieces_ordered.quantity,
          completed: item.pieces_ordered.completed,
        },
      }))
    : normalizedItemsOrderedDetails;

  const handlePiecesChange = (itemNo: string, newPieces: number) => {
    setPiecesQuantityOverrides((prev) => ({
      ...prev,
      [itemNo]: newPieces,
    }));
  };

  const handleConfirm = () => {
    if (Array.isArray(itemsOrderedDetails)) {
      onTaskChange({ details: { items: itemsOrderedDetails } });
    }
  };

  const { items, isFetchingItems } = useAllAttachedItems(
    itemNosAndTypeWithOrderedDetailsFromTask ?? LOADING
  );

  if (!shouldShowField(task, visibility)) {
    return null;
  }

  if (isFetchingItems || isLoading(itemNosAndTypeWithOrderedDetailsFromTask)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <ArticlesQuantity
      itemsNoAndTypeOrderedDetails={itemsOrderedDetails}
      items={items}
      handlePiecesChange={handlePiecesChange}
      handleConfirm={handleConfirm}
    />
  );
};

export default ArticlesQuantityCreatorFragment;
